import { analyticsEventTypes } from "../helper";

const isPreviewAnalyticsDisabled = () =>
  window?.__WEUPSELL_PUBLIC_PREVIEW__ === true ||
  window?.weupsellConfig?.disableAnalytics === true ||
  window?.Shopify?.designMode === true;

// Generic analytics utility for tracking popup events
export const trackImpression = async ({
  popupId,
  popupType,
  campaignId,
  addAnalyticsEvent
}) => {
  if (isPreviewAnalyticsDisabled()) {
    return;
  }

  if (!popupId || !addAnalyticsEvent) {
    console.warn('Analytics: Missing required parameters for impression tracking');
    return;
  }

  const storageKey = 'user-popup-impression';
  const userPopupImpressions = JSON.parse(localStorage.getItem(storageKey) || '[]');

  // In-memory per-load guard: prevents multiple counts within a single page load
  if (!window.__weupsellImpressionsOnce) {
    window.__weupsellImpressionsOnce = new Set();
  }
  if (window.__weupsellImpressionsOnce.has(popupId)) {
    return;
  }
  window.__weupsellImpressionsOnce.add(popupId);

  const analyticsPayload = {
    eventType: analyticsEventTypes.IMPRESSION,
    popupType: popupType,
    popupId: popupId,
    ...(campaignId && { campaignId: campaignId }),
  };

  try {
    // Append an entry with popupId and timestamp (always count, across refreshes and repeats)
    const impressionEntry = { popupId, timestamp: Date.now() };
    localStorage.setItem(storageKey, JSON.stringify([...userPopupImpressions, impressionEntry]));
    
    // Track the impression
    await addAnalyticsEvent(analyticsPayload);
    console.log(`Analytics: impression tracked for popup ${popupId}`);
  } catch (error) {
    console.error('Analytics: Error tracking impression:', error);
  }
};

/**
 * Track engagement clicks on upsell surfaces (image / title / button).
 * Counts every click — no per-load dedupe — so CTR reflects real engagement.
 */
export const trackClick = async ({
  popupId,
  popupType,
  campaignId,
  clickTarget,
  actionType,
  productId,
  variantId,
  addAnalyticsEvent,
}) => {
  if (isPreviewAnalyticsDisabled()) {
    return;
  }

  if (!popupId || !addAnalyticsEvent || !clickTarget) {
    console.warn("Analytics: Missing required parameters for click tracking");
    return;
  }

  const analyticsPayload = {
    eventType: analyticsEventTypes.CLICK,
    popupType,
    popupId,
    clickTarget,
    ...(campaignId && { campaignId }),
    ...(actionType && { actionType }),
    ...(productId != null && productId !== "" && { productId: String(productId) }),
    ...(variantId != null && variantId !== "" && { variantId: String(variantId) }),
  };

  try {
    await addAnalyticsEvent(analyticsPayload);
  } catch (error) {
    console.error("Analytics: Error tracking click:", error);
  }
};

export const trackConversion = async ({
  popupId,
  popupType,
  campaignId,
  addAnalyticsEvent
}) => {
  if (isPreviewAnalyticsDisabled()) {
    return;
  }

  if (!popupId || !addAnalyticsEvent) {
    console.warn('Analytics: Missing required parameters for conversion tracking');
    return;
  }

  const storageKey = 'user-popup-conversion';
  const userPopupConversions = JSON.parse(localStorage.getItem(storageKey) || '[]');

  // In-memory per-load guard: prevents multiple counts within a single page load
  if (!window.__weupsellConversionsOnce) {
    window.__weupsellConversionsOnce = new Set();
  }
  if (window.__weupsellConversionsOnce.has(popupId)) {
    return;
  }
  window.__weupsellConversionsOnce.add(popupId);

  const analyticsPayload = {
    eventType: analyticsEventTypes.CONVERSION,
    popupType: popupType,
    popupId: popupId,
    ...(campaignId && { campaignId: campaignId }),
  };

  try {
    // Append an entry with popupId and timestamp (always count, across refreshes and repeats)
    const conversionEntry = { popupId, timestamp: Date.now() };
    localStorage.setItem(storageKey, JSON.stringify([...userPopupConversions, conversionEntry]));

    // Track the conversion
    await addAnalyticsEvent(analyticsPayload);
    console.log(`Analytics: Conversion tracked for popup ${popupId}`);
  } catch (error) {
    console.error('Analytics: Error tracking conversion:', error);
  }
};
